/**
 * Anaesthesia pilot specialty – shared constants.
 *
 * IMPORTANT: field keys must match apps/backend/helpers/pilotSpecialties.js
 * (ANAESTHESIA_INTAKE_FIELDS), which drives report placeholder labels and the
 * admin template placeholder catalogue.
 */

export const ANAESTHESIA = 'Anaesthesia';

export type AnaesthesiaConsultSubType = 'anesthetic-planning' | 'pain-management';

export const ANAESTHESIA_CONSULT_SUBTYPES: { value: AnaesthesiaConsultSubType; label: string }[] = [
  { value: 'anesthetic-planning', label: 'Anesthesia planning consult' },
  { value: 'pain-management', label: 'Pain management consult' }
];

// specialtyIntakeData.specialtyType written by each intake form
export const ANAESTHESIA_SPECIALTY_TYPES: Record<AnaesthesiaConsultSubType, string> = {
  'anesthetic-planning': 'Anesthesia Planning Consult',
  'pain-management': 'Pain Management Consult'
};

export function getConsultSubTypeLabel(consultSubType: string | null | undefined): string {
  return ANAESTHESIA_CONSULT_SUBTYPES.find(o => o.value === consultSubType)?.label || '';
}

/** US presentation label; canonical specialty values remain unchanged for requests and matching. */
export function getSpecialtyDisplayName(speciality: string | string[] | null | undefined, consultSubType?: string | null): string {
  if (Array.isArray(speciality)) {
    return speciality.map(value => getSpecialtyDisplayName(value, consultSubType)).join(', ');
  }
  const label = speciality?.toLowerCase() === ANAESTHESIA.toLowerCase() ? getConsultSubTypeLabel(consultSubType) : '';
  const displayName = (speciality || '').replace(/\banaesthesia\b/gi, 'Anesthesia');
  return label ? `${displayName} – ${label}` : displayName;
}

// ---- Anesthesia planning consult (Mission – Pre-Surgical Anesthesia Intake Questionnaire) ----
export const PREVIOUS_ANESTHESIA_OPTIONS = ['Yes', 'No', 'Unknown'];
export const ASA_STATUS_OPTIONS = ['I', 'II', 'III', 'IV', 'V'];
export const ANESTHESIA_MACHINE_OPTIONS = ['Circle systems', 'Non-rebreathing systems', 'Multiple anesthesia machines', 'Unsure'];
export const TEMPERATURE_MONITORING = 'Temperature monitoring';
export const MONITORING_EQUIPMENT_OPTIONS = [
  'ECG', 'Pulse oximetry', 'Capnography', 'Sidestream capnography', 'Mainstream capnography',
  'Oscillometric BP', 'Doppler BP', TEMPERATURE_MONITORING
];
export const OTHER_OPIOIDS = 'Other opioids';
export const PREMEDICATION_SEDATION_OPTIONS = [
  'Dexmedetomidine', 'Medetomidine', 'Acepromazine', 'Midazolam', 'Diazepam', 'Butorphanol',
  'Hydromorphone', 'Methadone', 'Buprenorphine', 'Fentanyl', OTHER_OPIOIDS
];
export const OTHER_OPTION = 'Other';
export const INDUCTION_OPTIONS = ['Propofol', 'Alfaxalone', 'Ketamine', 'Etomidate', OTHER_OPTION];
export const REVERSAL_AGENT_OPTIONS = ['Atipamezole', 'Naloxone', 'Flumazenil', OTHER_OPTION];
export const LOCAL_REGIONAL_OPTIONS = ['Lidocaine', 'Bupivacaine', 'Liposomal bupivacaine', OTHER_OPTION];
export const EMERGENCY_DRUG_OPTIONS = ['Epinephrine', 'Atropine', 'Glycopyrrolate', 'Norepinephrine', 'Dopamine', 'Dobutamine'];

/** key -> label for each Anaesthesia intake form (keyed by specialtyType). */
export const ANAESTHESIA_FIELD_LABELS: Record<string, Record<string, string>> = {
  'Anesthesia Planning Consult': {
    plannedProcedureType: 'Planned Procedure Type',
    procedureDate: 'Date or Proposed Date of Procedure',
    previousAnesthesia: 'Has the Patient Had Anesthesia Previously?',
    lastPhysicalExamDate: 'Last Physical Examination Date',
    asaStatus: 'ASA Status',
    recentDiagnostics: 'Recent Diagnostics',
    currentMedications: 'Current Medications',
    anesthesiaMachineEquipment: 'Anesthesia Machine Equipment Available',
    monitoringEquipment: 'Monitoring Equipment Available',
    temperatureMonitoringTypes: 'Temperature Monitoring Types Available',
    mechanicalVentilator: 'Mechanical Ventilator Available',
    ventilatorMakeModel: 'Ventilator Make/Model',
    premedicationSedationDrugs: 'Premedication / Sedation Drugs Available',
    otherOpioids: 'Other Opioids',
    inductionDrugs: 'Induction Drugs Available',
    inductionOther: 'Other Induction Drugs',
    reversalAgents: 'Reversal Agents Available',
    reversalOther: 'Other Reversal Agents',
    localRegionalAnesthesia: 'Local / Regional Anesthesia Drugs Available',
    localRegionalOther: 'Other Local / Regional Anesthesia Drugs',
    emergencyDrugs: 'Emergency Drugs Available'
  },
  'Pain Management Consult': {
    painLocation: 'Where Does the Patient Appear to Be Painful?',
    primaryDiagnosis: 'Primary Diagnosis Driving the Pain',
    primaryPainMechanism: 'Primary Pain Mechanism',
    currentPainScore: 'Current Pain Score and Pain-Scoring System',
    mobilityChange: 'Change in Mobility from Normal',
    appetiteChange: 'Change in Appetite',
    sleepingResting: 'Sleeping / Resting',
    currentAnalgesicTherapy: 'Current Analgesic Therapy',
    nonPharmacologicalTreatments: 'Non-Pharmacological Treatments',
    previousDrugReactions: 'Previous Drug Reactions or Adverse Effects',
    organSystemConcerns: 'Renal, Hepatic, GI, Cardiovascular, Neurologic or Hematologic Concerns',
    clientMedicationComfort: 'Client Comfort Administering Medications'
  }
};
